import "./styles/SelectionProduct.scss";

import IconChevronLeft from "@/assets/icons/chevron-left.svg?react";
import IconNo from "@/assets/icons/no.svg?react";
import IconYes from "@/assets/icons/yes.svg?react";
import PlaceHolder from "@/assets/icons/placeholder.svg?react";
import uiHelper from "@/utils/uiHelper.tsx";
import useConfig from "@/features/config/hooks/useConfig.tsx";
import useTrackEvent from "@/features/widgets/hooks/useTrackEvent.tsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const CLASS_PREFIX = `ws-widgets-selection-product`;

type Props = {
  product: Product;
  label?: string | null;
  collapsible?: boolean;
}

const SelectionProduct = ({ product, label = null, collapsible = false }: Props) => {
  const config = useConfig();
  const price = product.price ? uiHelper.formatPrice(product.price, config.language, config.currency) : null;
  const trackEvent = useTrackEvent();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const open = !collapsible || expanded;
  // Show matching attributes first.
  const sortedMatches = [...product.matches].sort((a, b) => Number(b.matched) - Number(a.matched));

  return (
    <article className={ `${ CLASS_PREFIX } ${ collapsible ? `${ CLASS_PREFIX }--collapsible` : `` }` }>
      {
        collapsible ?
          <button
            className={ `${ CLASS_PREFIX }__title ${ CLASS_PREFIX }__title--toggle ${ CLASS_PREFIX }__title--${ expanded ? `expanded` : `not-expanded` }` }
            aria-expanded={ open }
            onClick={ () => setExpanded(!expanded) }
          >
            { product.name }
            <span className={ `${ CLASS_PREFIX }__title-icon ${ open ? `${ CLASS_PREFIX }__title-icon--open` : `` }` }>
              <IconChevronLeft/>
            </span>
          </button>
          :
          <div className={ `${ CLASS_PREFIX }__header` }>
            <a href={ product.url } rel="noopener noreferrer" onClick={ () => trackEvent(
                'click',
                'selection_dialog',
                product.name,
                [product],
              )
            }>
              <h4 className={ `${ CLASS_PREFIX }__title` }>
                { product.name }
              </h4>
            </a>
            {
              label &&
              <span className={ `${ CLASS_PREFIX }__label` }>
                { label }
              </span>
            }
          </div>
      }
      {
        open &&
        <>
          <div className={ `${ CLASS_PREFIX }__wrapper` }>

            <div className={ `${ CLASS_PREFIX }__image` }>
              { product.image ?
                <img src={ product.image } alt={ product.name }/>
                :
                <PlaceHolder/>
              }
            </div>
            <div className={ `${ CLASS_PREFIX }__content` }>
              {
                sortedMatches.length > 0 &&
                <ul className={ `${ CLASS_PREFIX }__attributes` }>
                  {
                    sortedMatches.map((match, index) =>
                        <li
                          key={ index }
                          className={ `${ CLASS_PREFIX }__attribute ${ CLASS_PREFIX }__attribute--${ match.matched ? `yes` : `no` } ${ CLASS_PREFIX }__attribute ${ CLASS_PREFIX }-attribute` }
                        >
                          <div className={`${ CLASS_PREFIX }-attribute__label` }>
                            {
                              match.matched ?
                                <IconYes className={ `${ CLASS_PREFIX }__attribute-icon` }/>
                                :
                                <IconNo className={ `${ CLASS_PREFIX }__attribute-icon` }/>
                            }
                            <span className={ `${ CLASS_PREFIX }__attribute-label` }>
                              { match.label }

                            </span>
                          </div>
                          <div className={`${ CLASS_PREFIX }-attribute__value` }>
                            <span className={ `${ CLASS_PREFIX }__attribute-value` }>
                              { match.value }
                            </span>
                          </div>
                        </li>
                    )
                  }
                </ul>
              }
            </div>
          </div>
          <div className={ `${ CLASS_PREFIX }__price` }>
            { price }
          </div>
          <div className={ `${ CLASS_PREFIX }__footer` }>
            <a
              className={ `ws-widgets-button ws-widgets-button--secondary` }
              href={ product.url }
              onClick={
                () =>
                  trackEvent(
                    `click`,
                    `selection_dialog_more_info`,
                    product.name,
                    [product],
                  )
              }
            >
              { t(`selection.button.product_info`) }
            </a>
            <a
              className={ `ws-widgets-button ws-widgets-button--primary` }
              href={ product.url }
              onClick={
                () =>
                  trackEvent(
                    `click`,
                    `selection_dialog_buy`,
                    product.name,
                    [product],
                  )
              }
            >
              { t(`core.button.buy`) }
            </a>
          </div>
        </>
      }
    </article>
  )
}

export default SelectionProduct;
